import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { WhyWorkWithUsSlider } from '@/components/WhyWorkWithUsSlider';
import { ValuesSlider } from '@/components/ValuesSlider';

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <div className="min-h-screen" data-testid="about-page">
      <Helmet>
        <title>About Us - Trademark Law Consulting | Experienced Trademark & Legal Services</title>
        <meta name="description" content="Learn about Trademark Law Consulting's mission to protect businesses across the USA. Expert trademark and legal services with proven track record, fast processing, and transparent pricing." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/8112131/pexels-photo-8112131.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-[#0A1C3E] opacity-90" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-[#E1A334] mb-4 block">
              About Our Firm
            </span>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white leading-tight mb-6">
              Protecting Identities,{' '}
              <span className="text-[#E1A334]">
                Securing Tomorrow
              </span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Trademark Law Consulting delivers complete trademark and legal support services designed to help businesses across the United States safeguard their most important asset—their brand identity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzY5NDYyMjIyfDA&ixlib=rb-4.1.0&q=85"
                alt="Our Team"
                className="rounded-3xl shadow-xl border border-slate-200 w-full h-auto"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
               Our Purpose
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                At Trademark Law Consulting, we strongly believe that every business deserves reliable trademark protection, no matter its size, stage, or industry. Our purpose is to make the trademark registration process simple, accessible, and completely stress-free for both startups and established companies.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                We recognize that your brand is built on years of effort, passion, and planning. That is why we handle every application with full dedication and precision, ensuring your intellectual property receives strong and lasting protection.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                Our experienced professionals continuously work through the complexities of trademark law, offering clear direction and dependable support at every stage. We are not just a service—we are your long-term partners in protecting and growing your brand identity.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
             Core Principles
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              The values that shape our work
            </p>
          </motion.div>

          <ValuesSlider />
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-8 text-center">
                Working Method
              </h2>
              
              <div className="space-y-8">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
                    Custom Solutions
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We avoid generic approaches. Every client receives dedicated attention with strategies designed specifically for their brand and business goals.
                  </p>
                </div>

                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
                    Clear Updates
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We maintain open and simple communication throughout the process, ensuring you always understand your application status and next steps.
                  </p>
                </div>

                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
                    Complete Safeguarding
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    From initial searches to final registration and ongoing monitoring, we provide full-cycle protection for your brand at every level.
                  </p>
                </div>

                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
                    Ongoing Support
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    Our commitment continues even after registration. We stay with you as your brand grows, offering continuous legal and protection assistance.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
             Why Choose Us
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Fast processing with most applications submitted within 24–48 hours after receiving complete details.
            </p>
          </motion.div>

          <WhyWorkWithUsSlider />
        </div>
      </section>
    </div>
  );
}
